/**
 * SVG → PNG, for embedding charts in the PDF and Word exports — Card P156.
 *
 * ⚠⚠ BROWSER ONLY, AND ISOLATED HERE FOR THAT REASON. Everything else in the
 * export path is pure and testable; this needs a real canvas and a real
 * image decoder, so it lives alone and every caller tolerates it returning
 * nothing. A chart that will not rasterise costs a picture, never the file.
 *
 * ⛔ No network. The SVG is serialised from the DOM we already rendered and
 * drawn through a data: URL — nothing is uploaded to rasterise a chart of
 * this clinic's revenue.
 */

/** Height of the burnt-in caption band, in unscaled pixels. */
const CAPTION_BAND = 42;

/*
 * ⛔⛔ CARD P227 B — THE CLONE MUST CARRY ITS OWN FONT.
 *
 * A rendered SVG inherits font-family from CSS on an ancestor. Serialising it
 * to a standalone data: URL throws that ancestor away, so the browser decodes
 * the image with NO font declared and falls back to its default serif — which
 * is why Daniel's first real PDF had Times axis labels inside a Helvetica
 * document, and why it read as a second typeface rather than as a bug. The
 * screen was always fine; only the capture was affected.
 *
 * ⚠ Declared on the ROOT and on every <text>/<tspan>, because a font-family
 * on an element that already carries its own does not cascade down to it.
 *
 * ⭐ ONE CAPTURE FUNCTION, SO B1'S "Download PNG" GETS THIS TOO. P220 B1 and
 * P227 landed in the same window and both touched this file — B1 adding the
 * burnt-in caption band below, P227 the font and the scale. The resolution is
 * a union rather than a pick: a captioned PNG in a serif face would be the
 * same defect wearing B1's clothes.
 */
const SVG_FONT = "Inter, system-ui, -apple-system, 'Segoe UI', Helvetica, Arial, sans-serif";

/**
 * Capture one element's <svg> as PNG bytes. Resolves null on any failure.
 *
 * ⚠ scale 3, not 2 (P227): the charts are rasterised at CSS pixel size and
 * then drawn into a PDF at roughly the same point size, so 2x left axis type
 * visibly soft on paper. Measured on the 4-page fixture report: 127KB -> about
 * 263KB for the whole document, which is nothing for a board pack — and B1's
 * standalone PNG download gets the same sharpening.
 */
export function captureSvgPng(el, { scale = 3, caption = null } = {}) {
  return new Promise((resolve) => {
    try {
      if (!el || typeof window === 'undefined' || !window.document) return resolve(null);
      const svg = el.querySelector('svg');
      if (!svg) return resolve(null);

      const rect = svg.getBoundingClientRect();
      const w = Math.max(1, Math.round(rect.width || svg.clientWidth || 600));
      const h = Math.max(1, Math.round(rect.height || svg.clientHeight || 220));

      const clone = svg.cloneNode(true);
      clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
      /* ⛔ P227 B — see SVG_FONT above. Without this the PNG is serif. */
      clone.setAttribute('font-family', SVG_FONT);
      clone.style.fontFamily = SVG_FONT;
      clone.querySelectorAll('text, tspan').forEach((t) => {
        t.setAttribute('font-family', SVG_FONT);
      });
      clone.setAttribute('width', String(w));
      clone.setAttribute('height', String(h));
      /* ⚠ A white ground: the charts are drawn for a light card, and a
         transparent PNG on a white page loses its axis labels. */
      const bg = window.document.createElementNS('http://www.w3.org/2000/svg', 'rect');
      bg.setAttribute('width', String(w)); bg.setAttribute('height', String(h));
      bg.setAttribute('fill', '#FFFFFF');
      clone.insertBefore(bg, clone.firstChild);

      const xml = new window.XMLSerializer().serializeToString(clone);
      const url = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(xml)}`;

      const img = new window.Image();
      const done = (v) => resolve(v);
      img.onload = () => {
        try {
          /*
           * ⭐ THE CAPTION IS DRAWN INTO THE IMAGE — Card P220 Part B.
           * Carlos drops these into a deck, where a chart with no title and
           * no period is a picture nobody can caption later. Burning it into
           * the pixels means the slide cannot drift from the data.
           */
          const head = caption && caption.title ? CAPTION_BAND : 0;
          const canvas = window.document.createElement('canvas');
          canvas.width = w * scale; canvas.height = (h + head) * scale;
          const ctx = canvas.getContext('2d');
          ctx.fillStyle = '#FFFFFF';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          if (head) {
            ctx.save();
            ctx.scale(scale, scale);
            ctx.fillStyle = '#374151';
            ctx.font = '600 13px Inter, system-ui, sans-serif';
            ctx.fillText(String(caption.title), 8, 18);
            if (caption.subtitle) {
              ctx.fillStyle = '#565D6B';
              ctx.font = '400 11px Inter, system-ui, sans-serif';
              ctx.fillText(String(caption.subtitle), 8, 34);
            }
            ctx.restore();
          }
          ctx.drawImage(img, 0, head * scale, w * scale, h * scale);
          const dataUrl = canvas.toDataURL('image/png');
          const b64 = dataUrl.split(',')[1] || '';
          const bin = window.atob(b64);
          const bytes = new Uint8Array(bin.length);
          for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
          done(bytes);
        } catch (e) { done(null); }
      };
      img.onerror = () => done(null);
      img.src = url;
    } catch (e) { resolve(null); }
  });
}

/**
 * Capture every chart on the Overview, keyed by section.
 * ⚠ Never rejects: a partial set of pictures is a fine report.
 */
export async function captureOverviewCharts(root) {
  const out = {};
  if (!root || typeof root.querySelector !== 'function') return out;
  const map = { trend: 'panel-trend', invoices: 'panel-invoices', team: 'panel-team' };
  for (const [key, testid] of Object.entries(map)) {
    try {
      const el = root.querySelector(`[data-testid="${testid}"]`);
      const png = await captureSvgPng(el);
      if (png) out[key] = png;
    } catch (e) { /* one missing chart is not a failed report */ }
  }
  return out;
}
